import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Logo from './Logo'
import { fetchNavigation } from '../lib/cms'

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [navItems, setNavItems] = useState([])
  const location = useLocation()
  const dropdownRef = useRef(null)

  // Default fallback navigation matching CMS structure
  const defaultNav = [
    { label: 'Forside', href: '/', order: 1, parent: null },
    {
      label: 'Ydelser',
      href: '#',
      order: 2,
      parent: null,
      children: [
        { label: 'Temperaturstyret transport', href: '/temperaturstyret-transport', description: '-20°C til +25°C præcis temperaturstyring' },
        { label: 'Ekspreslevering 24/7', href: '/ekspreslevering', description: 'Afgang på ruten inden for 2 timer' },
      ],
    },
    { label: 'Vores biler', href: '/vores-biler', order: 3, parent: null },
    { label: 'Om Transport4U', href: '/om-transport4u', order: 4, parent: null },
    { label: 'Kontakt', href: '/kontakt', order: 5, parent: null },
  ]

  useEffect(() => {
    let isMounted = true
    async function loadNav() {
      const items = await fetchNavigation()
      if (isMounted && items && items.length > 0) {
        // Group items by parent
        const parents = items.filter(i => !i.parent)
        const children = items.filter(i => !!i.parent)
        const structured = parents.map(p => {
          const matchedKids = children.filter(c => c.parent === p.label)
          return {
            ...p,
            children: matchedKids.length > 0 ? matchedKids : undefined,
          }
        })
        setNavItems(structured)
      } else {
        setNavItems(defaultNav)
      }
    }
    loadNav()
    return () => { isMounted = false }
  }, [])

  const navigation = navItems.length > 0 ? navItems : defaultNav

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
    setDropdownOpen(false)
  }, [location.pathname])

  // Handle click outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  const isChildActive = (children) => {
    if (!children) return false
    return children.some(c => location.pathname === c.href)
  }

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-xs">
      {/* Main navigation container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="shrink-0">
            <Logo variant="dark" />
          </div>

          {/* Desktop Navigation: VISES FØRST VED >= 1200px */}
          <nav className="hidden min-[1200px]:flex items-center gap-8" aria-label="Hovednavigation">
            {navigation.map((item) => {
              if (item.children && item.children.length > 0) {
                const parentActive = isChildActive(item.children)
                return (
                  <div key={item.label} className="relative" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors py-2 px-1 focus:outline-none cursor-pointer ${
                        parentActive
                          ? 'text-sky-600 font-bold border-b-2 border-sky-500'
                          : 'text-slate-700 hover:text-sky-600'
                      }`}
                      aria-expanded={dropdownOpen}
                    >
                      <span>{item.label}</span>
                      <i
                        className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                          dropdownOpen ? 'rotate-180 text-sky-600' : 'text-slate-600'
                        }`}
                        aria-hidden="true"
                      />
                    </button>

                    {/* Dropdown Menu */}
                    {dropdownOpen && (
                      <div className="absolute top-full left-0 mt-2 w-72 rounded-xl bg-white p-3 shadow-xl border border-slate-200/90 ring-1 ring-black/5 z-50">
                        <div className="space-y-1">
                          {item.children.map((child) => {
                            const active = location.pathname === child.href
                            return (
                              <Link
                                key={child.href}
                                to={child.href}
                                onClick={() => setDropdownOpen(false)}
                                className={`block p-3 rounded-lg transition-colors ${
                                  active
                                    ? 'bg-sky-50 text-sky-700 font-semibold'
                                    : 'text-slate-700 hover:bg-slate-50 hover:text-sky-600'
                                }`}
                              >
                                <div className="text-sm font-medium flex items-center justify-between">
                                  <span>{child.label}</span>
                                  {active && (
                                    <i className="fa-solid fa-check text-xs text-sky-600" aria-hidden="true" />
                                  )}
                                </div>
                                {child.description && (
                                  <p className="text-xs text-slate-700 mt-0.5 line-clamp-1">
                                    {child.description}
                                  </p>
                                )}
                              </Link>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              }

              const active = isActive(item.href)
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`text-sm font-semibold transition-colors py-2 px-1 relative ${
                    active
                      ? 'text-sky-600 font-bold'
                      : 'text-slate-700 hover:text-sky-600'
                  }`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-500 rounded-full" />
                  )}
                </Link>
              )
            })}
          </nav>

          {/* Desktop Right Side CTAs: VISES FØRST VED >= 1200px */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <a
              href="tel:+4550352035"
              className="inline-flex items-center gap-2 text-sm font-bold text-navy-900 hover:text-sky-600 px-3 py-2 transition-colors whitespace-nowrap"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                <i className="fa-solid fa-phone" aria-hidden="true" />
              </div>
              <span className="whitespace-nowrap">50 35 20 35</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-sky-600 text-white text-sm font-semibold hover:bg-sky-500 active:bg-sky-700 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40"
            >
              <span>Få et tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile / Tablet Actions: VISES NÅR SKÆRMEN ER MINDRE END 1200px */}
          <div className="flex min-[1200px]:hidden items-center gap-2.5">
            <a
              href="tel:+4550352035"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-50 text-sky-700 text-xs font-bold border border-sky-200/80 whitespace-nowrap"
              aria-label="Ring til 50 35 20 35"
            >
              <i className="fa-solid fa-phone text-sky-600" aria-hidden="true" />
              <span className="whitespace-nowrap">50 35 20 35</span>
            </a>

            <Link
              to="/kontakt"
              className="hidden sm:inline-flex items-center px-3.5 py-2 rounded-lg bg-sky-600 text-white text-xs font-semibold hover:bg-sky-500 transition-colors whitespace-nowrap"
            >
              Bestil 24/7
            </Link>

            {/* Burger menu button */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2.5 rounded-lg text-slate-700 hover:text-sky-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn navigationsmenu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer (under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-slate-200 bg-white shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 pt-3 pb-6 space-y-1">
            {navigation.map((item) => {
              if (item.children && item.children.length > 0) {
                return (
                  <div key={item.label} className="py-2 border-b border-slate-100">
                    <div className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-600">
                      {item.label}
                    </div>
                    <div className="pl-2 space-y-1 mt-1">
                      {item.children.map((child) => {
                        const active = location.pathname === child.href
                        return (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium ${
                              active
                                ? 'bg-sky-50 text-sky-700 font-semibold'
                                : 'text-slate-700 hover:bg-slate-50 hover:text-sky-600'
                            }`}
                          >
                            <span>{child.label}</span>
                            {active && (
                              <i className="fa-solid fa-check text-xs text-sky-600" aria-hidden="true" />
                            )}
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                )
              }

              const active = isActive(item.href)
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center justify-between px-3 py-3 rounded-lg text-sm font-semibold border-b border-slate-100 last:border-0 ${
                    active
                      ? 'bg-sky-50 text-sky-700'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-sky-600'
                  }`}
                >
                  <span>{item.label}</span>
                  {active && (
                    <span className="h-1.5 w-1.5 rounded-full bg-sky-600"></span>
                  )}
                </Link>
              )
            })}

            {/* Mobile Contact & CTA Box */}
            <div className="pt-4 mt-4 border-t border-slate-200 space-y-3">
              <a
                href="tel:+4550352035"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-slate-100 text-navy-900 font-bold text-sm hover:bg-slate-200 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-sky-600" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring 50 35 20 35 (Døgnvagt)</span>
              </a>

              <Link
                to="/kontakt"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-sky-600 text-white font-bold text-sm hover:bg-sky-500 transition-colors shadow-md"
              >
                <span>Få et uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
